import { ReactNode, useEffect, useState } from "react";

import john from "@/../../hypetrain/static/img/coaching-platform/john.png";
import mark from "@/../../hypetrain/static/img/coaching-platform/mark.png";
import drew from "@/../../hypetrain/static/img/exec-home/drew.webp";
import jennifer from "@/../../hypetrain/static/img/exec-home/jennifer.webp";
import kim from "@/../../hypetrain/static/img/exec-home/kim.webp";
import lauren from "@/../../hypetrain/static/img/exec-home/lauren.webp";
import margaret from "@/../../hypetrain/static/img/exec-home/margaret.webp";
import tommy from "@/../../hypetrain/static/img/exec-home/tommy.webp";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { Container } from "@/components/pieces/bootstrap";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { QuoteIcon } from "@/components/pieces/marketing/QuoteIcon";
import { StyledTitle } from "@/components/pieces/marketing/Titles";

import {
  AxoniusLogo,
  CockroachLogo,
  ConveneLogo,
  EkoLogo,
  PhmLogo,
  TradestationLogo,
  XencorLogo,
} from "./partner-logos";
import { ResultsCxLogo } from "./partner-logos/ResultsCxLogo";

// Partner testimonial interface
interface PartnerTestimonial {
  id: string;
  companyName: string;
  quote: string;
  personName: string;
  personTitle: string;
  personPhoto: string;
  logo:
    | "axonius"
    | "phm"
    | "convene"
    | "xencor"
    | "eko"
    | "cockroach"
    | "resultscx"
    | "tradestation";
}

// Sample testimonial data
const TESTIMONIALS: PartnerTestimonial[] = [
  {
    id: "phm",
    companyName: "PHM",
    quote:
      "As a sales leader, I'm always looking for ways to get new reps ramped faster. Exec's AI Roleplay platform gives our people a safe space to practice high-stakes conversations before getting in front of real prospects...The immediate feedback and ability to retry scenarios until mastery has been a game changer for our team's development and speed to market.",
    personName: "Tommy Axford",
    personTitle: "Chief Commercial Officer at Private Health Management",
    logo: "phm",
    personPhoto: tommy,
  },
  {
    id: "axonius",
    companyName: "Axonius",
    quote:
      "Exec empowers Axonius to deliver on our 'human-led, tech-enabled' talent development promise. Their streamlined platform and network of top-tier coaches are unmatched. We're now able to deliver highly personalized learning experiences at scale, all while delivering a tangible return on our investment.",
    personName: "Drew Fifield",
    personTitle: "Head of Global Talent Development at Axonius",
    logo: "axonius",
    personPhoto: drew,
  },
  {
    id: "convene",
    companyName: "Convene",
    quote:
      "Exec has become an invaluable strategic partner in our efforts to develop talent at Convene. Their ability to understand our business needs and deliver customized solutions - from executive coaching to leadership facilitation - has helped us scale our L&D initiatives while maintaining exceptional quality.",
    personName: "Margaret Rafferty",
    personTitle: "Chief People Officer at Convene",
    logo: "convene",
    personPhoto: margaret,
  },
  {
    id: "xencor",
    companyName: "Xencor",
    quote:
      "What sets Exec apart is how they blend expert facilitation with innovative AI Roleplays to create a truly effective training. The mix of interactive sessions and applicable AI-practice has given our managers the assurance and tools to have more meaningful conversations with their teams. The consistently high ratings from our employees speak to how effectively this approach builds both confidence and competence in giving and receiving feedback.",
    personName: "Jennifer Sandoz",
    personTitle: "Senior Vice President of Human Resources at Xencor",
    logo: "xencor",
    personPhoto: jennifer,
  },
  {
    id: "eko",
    companyName: "Eko",
    quote:
      "They truly understand what we need, whether it's designing specialized coaching programs or scaling practice through AI Roleplays. Their combination of exceptional coaches, innovative technology, and hands-on partnership approach means we can focus on what matters most - developing our people.",
    personName: "Kim Nolan",
    personTitle: "Senior Vice President of People at Eko Health",
    logo: "eko",
    personPhoto: kim,
  },
  {
    id: "cockroach",
    companyName: "Cockroach Labs",
    quote:
      "We are so excited to have Exec supporting our talent development initiatives. They provide us the agile infrastructure to simultaneously address company-wide challenges like manager enablement, niche team requests, and individual employee needs. The coaches they bring have credibility from serving in functions that are relevant to our employees.  Their team is fantastic in all aspects of service.",
    personName: "Lauren Covert Weber",
    personTitle:
      "Director of People Operations & Development at Cockroach Labs",
    logo: "cockroach",
    personPhoto: lauren,
  },
  {
    id: "resultscx",
    companyName: "ResultsCX",
    quote:
      "The feedback we've been getting from our employees has been amazing. Exec's programs are aligned with our culture. They understand how to help our people develop as leaders.",
    personName: "Mark Cunningham",
    personTitle: "CPO at ResultsCX",
    logo: "resultscx",
    personPhoto: mark,
  },
  {
    id: "tradestation",
    companyName: "TradeStation",
    quote:
      "Exec has become our go-to provider for training and development. With their platform weʼve been able to quickly launch programs that match the needs of our employees and start getting results immediately.",
    personName: "John Bartleman",
    personTitle: "CEO at TradeStation",
    logo: "tradestation",
    personPhoto: john,
  },
];

function PartnerLogo({ logoName }: { logoName: PartnerTestimonial["logo"] }) {
  switch (logoName) {
    case "axonius":
      return <AxoniusLogo height={24} />;
    case "cockroach":
      return <CockroachLogo height={24} />;
    case "convene":
      return <ConveneLogo height={22} />;
    case "eko":
      return <EkoLogo height={36} />;
    case "phm":
      return <PhmLogo height={32} />;
    case "xencor":
      return <XencorLogo height={24} />;
    case "resultscx":
      return <ResultsCxLogo height={32} />;
    case "tradestation":
      return <TradestationLogo height={24} />;
    default:
      return null;
  }
}

interface PartnerTestimonialsProps {
  partnerIds?: string[]; // Optional array of partner IDs
  tint?: "teal" | "blue" | "purple" | "green" | "red" | "orange" | "gray";
  bottomCta?: ReactNode;
}

export function PartnerTestimonials({
  partnerIds,
  tint = "teal",
  bottomCta,
}: PartnerTestimonialsProps) {
  // Define color mappings for different tints
  const tintColors = {
    teal: {
      background: "bg-teal-800",
      text: "text-white",
      buttonActive: "bg-white/100 text-teal-800",
      buttonInactive:
        "text-white opacity-50 hover:bg-white/10 hover:opacity-100",
      subtext: "text-teal-200",
      avatar: "bg-teal-500/80",
    },
    blue: {
      background: "bg-blue-900",
      text: "text-white",
      buttonActive: "bg-white/100 text-blue-900",
      buttonInactive:
        "text-white opacity-50 hover:bg-white/10 hover:opacity-100",
      subtext: "text-blue-200",
      avatar: "bg-blue-500/80",
    },
    purple: {
      background: "bg-purple-800",
      text: "text-white",
      buttonActive: "bg-white/100 text-purple-800",
      buttonInactive:
        "text-white opacity-50 hover:bg-white/10 hover:opacity-100",
      subtext: "text-purple-200",
      avatar: "bg-purple-500/80",
    },
    green: {
      background: "bg-green-800",
      text: "text-white",
      buttonActive: "bg-white/100 text-green-800",
      buttonInactive:
        "text-white opacity-50 hover:bg-white/10 hover:opacity-100",
      subtext: "text-green-200",
      avatar: "bg-green-500/80",
    },
    red: {
      background: "bg-red-800",
      text: "text-white",
      buttonActive: "bg-white/100 text-red-800",
      buttonInactive:
        "text-white opacity-50 hover:bg-white/10 hover:opacity-100",
      subtext: "text-red-200",
      avatar: "bg-red-500/80",
    },
    orange: {
      background: "bg-orange-800",
      text: "text-white",
      buttonActive: "bg-white/100 text-orange-800",
      buttonInactive:
        "text-white opacity-50 hover:bg-white/10 hover:opacity-100",
      subtext: "text-orange-200",
      avatar: "bg-orange-500/80",
    },
    gray: {
      background: "bg-gray-800",
      text: "text-white",
      buttonActive: "bg-white/100 text-gray-800",
      buttonInactive:
        "text-white opacity-50 hover:bg-white/10 hover:opacity-100",
      subtext: "text-gray-200",
      avatar: "bg-gray-500/80",
    },
  };

  const colors = tintColors[tint];
  // Filter testimonials based on provided partnerIds, or use all if not provided
  const displayedTestimonials = partnerIds
    ? TESTIMONIALS.filter((t) => partnerIds.includes(t.id))
        // Sort according to the order in partnerIds array
        .sort((a, b) => partnerIds.indexOf(a.id) - partnerIds.indexOf(b.id))
    : TESTIMONIALS;

  // Use first testimonial in the filtered list, or first overall if empty
  const initialTestimonial =
    displayedTestimonials.length > 0
      ? displayedTestimonials[0].id
      : TESTIMONIALS[0].id;

  const [activePartner, setActivePartner] = useState(initialTestimonial);
  const [autoProgress, setAutoProgress] = useState(true);

  // Find the current active testimonial
  const activeTestimonial =
    displayedTestimonials.find(
      (testimonial) => testimonial.id === activePartner,
    ) ||
    displayedTestimonials[0] ||
    TESTIMONIALS[0];

  // Auto-progress through testimonials
  useEffect(() => {
    if (autoProgress && displayedTestimonials.length > 1) {
      const interval = setInterval(() => {
        const currentIndex = displayedTestimonials.findIndex(
          (t) => t.id === activePartner,
        );
        const nextIndex = (currentIndex + 1) % displayedTestimonials.length;
        setActivePartner(displayedTestimonials[nextIndex].id);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [autoProgress, activePartner, displayedTestimonials]);

  const handlePartnerClick = (partnerId: string) => {
    setAutoProgress(false);
    setActivePartner(partnerId);
  };

  const longestQuote = displayedTestimonials.reduce((longest, current) => {
    return current.quote.length > longest.length ? current.quote : longest;
  }, "");

  return (
    <div className={`${colors.background} pt-16 pb-32 ${colors.text}`}>
      <Container className="!max-w-marketing-content">
        <StyledTitle className="mb-12 text-white">
          Achieve more with Exec
        </StyledTitle>

        {/* Partner logos */}
        <div className="mb-8 rounded-lg bg-white/5 p-2 transition-all duration-300">
          <div
            className={`grid grid-cols-2 gap-2 sm:grid-cols-3 ${displayedTestimonials.length === 1 ? "sm:grid-cols-1" : ""} ${displayedTestimonials.length === 2 ? "sm:grid-cols-2" : ""} ${displayedTestimonials.length === 3 ? "sm:grid-cols-3" : ""} ${displayedTestimonials.length === 4 ? "md:grid-cols-4" : ""} ${displayedTestimonials.length === 5 ? "lg:grid-cols-5" : ""} ${displayedTestimonials.length >= 6 ? "lg:grid-cols-6" : ""} `}
          >
            {displayedTestimonials.map((partner) => (
              <Clickable
                key={partner.id}
                onClick={() => handlePartnerClick(partner.id)}
              >
                <div
                  className={`flex h-10 w-full cursor-pointer items-center justify-center rounded-md px-4 py-7 transition-all duration-300 ${activePartner === partner.id ? colors.buttonActive : colors.buttonInactive}`}
                >
                  <PartnerLogo logoName={partner.logo} />
                </div>
              </Clickable>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white/5 p-6 transition-all duration-500 before:absolute before:bottom-[-140px] before:left-[-140px] before:z-1 before:h-[300px] before:w-[300px] before:rounded-full before:bg-white/[0.03] before:content-[''] after:absolute after:top-[-60px] after:right-[-80px] after:z-1 after:h-[250px] after:w-[250px] after:rounded-full after:bg-white/[0.02] after:content-[''] md:p-8 lg:p-12">
          <div className="relative mx-auto flex max-w-5xl gap-8">
            <div className="hidden opacity-20 md:block">
              <QuoteIcon height={36} width={42} color="#FFF" />
            </div>
            <div className="h-auto transition-all duration-500">
              <div className="relative">
                <p className="text-lg leading-relaxed opacity-0 md:text-xl lg:text-2xl">
                  {longestQuote}
                </p>
                <p className="absolute top-0 left-0 h-full w-full text-lg leading-relaxed md:text-xl lg:text-2xl">
                  {activeTestimonial.quote}
                </p>
              </div>

              <div className="mt-8 flex items-center">
                <div
                  className={`mr-4 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full ${colors.avatar} text-xs text-white`}
                >
                  <LazyImg
                    src={activeTestimonial.personPhoto}
                    width="60"
                    height="60"
                  />
                </div>
                <div>
                  <div className="font-semibold">
                    {activeTestimonial.personName}
                  </div>
                  <div className={`mt-1 text-sm ${colors.subtext}`}>
                    {activeTestimonial.personTitle}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {bottomCta}
      </Container>
    </div>
  );
}
